import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage, useReducedMotion } from '@ai-tutor/ui';
import { useNow } from '@/hooks/ui/useNow';
import { formatClock } from '@/lib/focus/format';
import { playBreathCue } from '@/lib/wellbeing/breathCue';
import { WellbeingRoom } from '../WellbeingRoom';
import { useSpaceShortcut } from '../focus/useSpaceShortcut';
import { useWellbeingKeys } from '../wellbeingStorage';
import {
  BREATHING_PATTERNS,
  breathAt,
  breathsIn,
  cycleSeconds,
} from './breathingPatterns';
import { BreatheStageView, type BreatheRun } from './BreatheStageView';
import {
  DEFAULT_BREATHE_PREFS,
  type BreatheFeeling,
  type BreathePrefs,
  type BreatheStatus,
} from './breatheTypes';

/** Where a session is, `elapsed` ms into `totalMs`. */
function runAt(
  prefs: BreathePrefs,
  elapsed: number,
  totalMs: number
): BreatheRun {
  const pattern = BREATHING_PATTERNS[prefs.pattern];
  const position = breathAt(pattern, elapsed);
  const total = breathsIn(pattern, totalMs);
  const cycleMs = cycleSeconds(pattern) * 1000;
  return {
    cue: position.step.cue,
    stepIndex: position.stepIndex,
    scale: position.step.scale,
    stepSeconds: position.step.seconds,
    stepProgress: position.stepProgress,
    countdown: Math.max(1, Math.ceil(position.stepRemaining)),
    breath: {
      current: Math.min(total, Math.floor(elapsed / cycleMs) + 1),
      total,
    },
    sessionProgress: elapsed / totalMs,
    remainingLabel: formatClock(totalMs - elapsed),
  };
}

/**
 * The soft cue at each change of breath, when the student has asked for it.
 * Keyed on the step's position in the session, not its words: box breathing
 * says "Hold" twice per breath.
 */
function useBreathCue(run: BreatheRun | undefined, on: boolean) {
  const key = run ? `${run.breath.current}:${run.stepIndex}` : null;
  const rising = run ? run.scale > 0.6 : false;
  const last = useRef<string | null>(null);
  useEffect(() => {
    const changed = key !== null && key !== last.current;
    last.current = key;
    if (on && changed) playBreathCue(rising);
  }, [key, on, rising]);
}

/**
 * /wellbeing/breathe, wired: owns the session clock and the remembered
 * choices, and hands `BreatheStageView` where in the breath it is. A session
 * is short and does not survive navigation, on purpose - it is something you
 * do now, not something to resume.
 */
export function BreatheRoom() {
  const keys = useWellbeingKeys();
  const [stored, setStored] = useLocalStorage<BreathePrefs>(
    keys.breathe,
    DEFAULT_BREATHE_PREFS
  );
  const prefs = useMemo(
    () => ({ ...DEFAULT_BREATHE_PREFS, ...stored }),
    [stored]
  );
  const [status, setStatus] = useState<BreatheStatus>('idle');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [feeling, setFeeling] = useState<BreatheFeeling | null>(null);
  const reducedMotion = useReducedMotion();

  const running = status === 'running' && startedAt !== null;
  // 200ms: the cue has to change on the beat, not up to a second late.
  const now = useNow(running, 200);
  const totalMs = Number(prefs.length) * 60_000;
  const elapsed = running ? Math.min(now - startedAt, totalMs) : 0;
  const finished = running && now - startedAt >= totalMs;
  const run = running ? runAt(prefs, elapsed, totalMs) : undefined;

  useEffect(() => {
    if (finished) {
      setStatus('done');
      setStartedAt(null);
    }
  }, [finished]);
  useBreathCue(run, prefs.sound);

  const start = () => {
    setFeeling(null);
    setStartedAt(Date.now());
    setStatus('running');
  };
  const stop = () => {
    setStartedAt(null);
    setStatus('idle');
  };
  useSpaceShortcut(status, { idle: start, running: stop, done: start });

  return (
    <WellbeingRoom
      title='Breathe'
      description='Sit comfortably. Follow the circle and the words.'
      dimmed={running}
    >
      <BreatheStageView
        status={status}
        prefs={prefs}
        run={run}
        feeling={feeling}
        reducedMotion={reducedMotion}
        onPrefsChange={setStored}
        onStart={start}
        onStop={stop}
        onFeeling={setFeeling}
      />
    </WellbeingRoom>
  );
}
